import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { FormProvider, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { parseISO } from "date-fns";
import { Outlet, useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";

import CircularProgress from "@mui/material/CircularProgress";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { Toast } from "@covertree/utils";
import { useFormWithNotification } from "@covertree/hooks";
import { PolicyUsage } from "@covertree/agent-portal-graphql";
import type { StateCode } from "@covertree/consts";

import {
  buildInsuredTabFormValuesFromPolicy,
  checkPrice,
  createOrUpdateEndorsement,
  selectEndorsement,
  selectEndorsementEffectiveDateBounds,
  selectEndorsementId,
  selectIsRecalculating,
  selectIsViewOnly,
  selectMode,
  selectShouldCheckUnderwriting,
  setHasUnsavedChanges,
  setInsuredTabDetails,
  setInsuredTabDetailsFromPolicy,
  useEndorsementSelector,
} from "@redux/slices/endorsement";
import { clearHiddenExtraCoverages } from "@redux/slices/endorsement/extraCoverageFields";
import { useAppDispatch } from "@redux/hooks";
import { EndorsementRoutes, navigateToEndorsementStep } from "@routing/paths";

import { useCheckUnderwriting } from "./hooks/useCheckUnderwriting";
import type { PolicyDetailsContentProps } from "@routing/routes/Policies/PolicyDetails/PolicyDetailsContent/types";
import { getInsuredTabFormSchema } from "@layouts/EndorsementLayout/utils/getInsuredTabFormSchema";

import { getEndorsementErrorMessage } from "./utils/endorsementErrorMessage";
import {
  EndorsementFormHandle,
  EndorsementFormValues,
  EndorsementOutletContext,
} from "./types";

interface EndorsementFormProps {
  policy?: PolicyDetailsContentProps["policy"];
  policyLoading: boolean;
  policyError: boolean;
  currentStep: EndorsementRoutes;
  nextStep?: EndorsementRoutes;
  isLastStep: boolean;
  isEditMode: boolean;
  onClose: () => void;
  onSaveDraftError: () => void;
}

type LoadedPolicy = NonNullable<PolicyDetailsContentProps["policy"]>;

type EndorsementFormFieldsProps = Omit<
  EndorsementFormProps,
  "policy" | "policyLoading" | "policyError"
> & { policy: LoadedPolicy };

export const EndorsementForm = forwardRef<
  EndorsementFormHandle,
  EndorsementFormProps
>(function EndorsementForm(
  { policy, policyLoading, policyError, ...rest },
  ref
) {
  if (policyLoading && !policyError) {
    return (
      <Stack alignItems="center" justifyContent="center" height={1}>
        <CircularProgress />
      </Stack>
    );
  }

  if (policyError || !policy) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        gap={1}
        height={1}
        textAlign="center">
        <ErrorOutlineIcon color="error" fontSize="large" />
        <Typography variant="h5" fontSize={16}>
          Couldn’t load this policy
        </Typography>
        <Typography color="text.secondary">
          Something went wrong while loading the policy. Please close and try
          again.
        </Typography>
      </Stack>
    );
  }

  return <EndorsementFormFields {...rest} policy={policy} ref={ref} />;
});

const EndorsementFormFields = forwardRef<
  EndorsementFormHandle,
  EndorsementFormFieldsProps
>(function EndorsementFormFields(
  {
    policy,
    currentStep,
    nextStep,
    isLastStep,
    isEditMode,
    onClose,
    onSaveDraftError,
  },
  ref
) {
  const { policyLocator = "" } = useParams();
  const [searchParams] = useSearchParams();
  const endorsementId = searchParams.get("endorsementId");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const checkUnderwriting = useCheckUnderwriting(policyLocator, endorsementId);

  // Only one unit exists today, matching the rest of the flow. The backend
  // needs it to address the lot/unit + construction changes.
  const unitId = policy.units?.[0]?.unitId;

  const mode = useEndorsementSelector(selectMode);
  const isViewOnly = useEndorsementSelector(selectIsViewOnly);
  const shouldCheckUnderwriting = useEndorsementSelector(
    selectShouldCheckUnderwriting
  );
  const isRecalculating = useEndorsementSelector(selectIsRecalculating);
  const endorsementIdState = useEndorsementSelector(selectEndorsementId);
  const effectiveDateBounds = useEndorsementSelector(
    selectEndorsementEffectiveDateBounds
  );
  // A loaded draft's saved Insured Info edits (update/issue); undefined otherwise.
  // Carried on the loaded endorsement and merged over the policy baseline below
  // to build the form defaults.
  const endorsement = useEndorsementSelector(selectEndorsement);

  // Validation for the Insured Info step mirrors the new-business agent rules and
  // depends on the policy (tenant flow, state, term bounds).
  const insuredTabResolver = useMemo(
    () =>
      yupResolver(
        getInsuredTabFormSchema({
          isTenant: policy.mainUnitType === PolicyUsage.Tenant,
          state: policy.units?.[0]?.address?.state as StateCode | undefined,
          ...effectiveDateBounds,
        })
        // The schema validates a subset of the form (no `name`/
        // `additionalInterests`), so its inferred type is narrower than the
        // form's — an expected yup↔RHF gap, not a form-shape mismatch.
      ) as unknown as Resolver<EndorsementFormValues>,
    [policy, effectiveDateBounds]
  );

  // Defaults are the policy baseline with a loaded draft's saved edits overlaid
  // on top — an endorsement always starts from the policy's current values, and
  // update/issue re-hydrate the draft's own edits over them. Merging here (rather
  // than relying on redux write order) keeps it correct regardless of whether the
  // policy seed or the draft load lands first.
  const insuredTabDefaults = useMemo(() => {
    const overlay = endorsement?.insuredTabOverlay;
    const draftDate = endorsement?.insuredTabOverlay?.endorsementEffectiveDate;

    return {
      ...buildInsuredTabFormValuesFromPolicy(policy),
      ...overlay,
      ...(typeof draftDate === "string" && {
        endorsementEffectiveDate: parseISO(draftDate),
      }),
    };
  }, [policy, endorsement?.insuredTabOverlay]);

  const form = useFormWithNotification<EndorsementFormValues>({
    mode: "all",
    reValidateMode: "onSubmit",
    shouldUnregister: false,
    defaultValues: insuredTabDefaults,
    resolver:
      currentStep === EndorsementRoutes.INSURED_INFO
        ? insuredTabResolver
        : undefined,
  });

  // Seed the redux copy of the Insured Info values from the policy. This is not
  // the form's baseline — that is `insuredTabDefaults` above, which overlays the
  // draft. Redux only needs the policy here: its sole reader until the agent
  // submits step 1 is `selectInsuredTabAdditionalInterests` (the mortgagee
  // payment option). `setInsuredTabDetails` in `handleNext` replaces this with
  // what the agent actually submitted.
  useEffect(() => {
    if (policy) {
      dispatch(setInsuredTabDetailsFromPolicy(policy));
    }
  }, [dispatch, policy]);

  // Identifies the source of `insuredTabDefaults`: policy baseline, or baseline
  // with a loaded draft's overlay. Hydration runs once per source.
  const hydrationKey = `${policy.policyLocator}|${endorsementId ?? "new"}|${
    endorsement?.insuredTabOverlay ? "draft" : "baseline"
  }`;

  // Hydrates the Insured Info values (the Coverage step seeds its own fields in
  // `useSeedBaseDefaults`). Runs only when the source changes: re-running it
  // after a reprice blanked the effective date the agent had typed.
  //
  // Save responses are never written back into the form; a stale projected
  // value once overwrote the agent's input and was re-posted on the next save.
  // While editing, the form is the source of truth (see `insuredTabProjection`).
  const hydratedKey = useRef<string | null>(null);

  useEffect(() => {
    const canHydrate =
      currentStep === EndorsementRoutes.INSURED_INFO ||
      (isViewOnly && !!endorsement?.insuredTabOverlay);
    if (!canHydrate) return;
    if (hydratedKey.current === hydrationKey) return;

    hydratedKey.current = hydrationKey;
    // Merge, since `insuredTabDefaults` has no coverage fields and a plain
    // `reset` would drop them. `keepDirtyValues` protects edits made while the
    // async draft load was still in flight.
    form.reset(
      { ...form.getValues(), ...insuredTabDefaults },
      { keepDirtyValues: true }
    );
  }, [
    currentStep,
    mode,
    endorsement?.insuredTabOverlay,
    hydrationKey,
    insuredTabDefaults,
    form,
  ]);

  const { minDate, maxDate } = effectiveDateBounds;

  useEffect(() => {
    if (mode !== "create" || !minDate) return;

    if (!form.getValues("endorsementEffectiveDate")) {
      form.setValue("endorsementEffectiveDate", minDate);
    }
  }, [form, mode, minDate]);

  const effectiveDate = form.watch("endorsementEffectiveDate");

  useEffect(() => {
    if (mode !== "update" || !minDate || !effectiveDate) return;

    const date =
      typeof effectiveDate === "string"
        ? parseISO(effectiveDate)
        : effectiveDate;
    if (date < minDate || (maxDate && date > maxDate)) {
      form.setValue("endorsementEffectiveDate", null);
    }
  }, [form, mode, minDate, maxDate, effectiveDate]);

  const hasDirtyFields = Object.keys(form.formState.dirtyFields).length > 0;

  useEffect(() => {
    if (!isEditMode) return;
    dispatch(setHasUnsavedChanges(hasDirtyFields));
  }, [dispatch, isEditMode, hasDirtyFields]);

  // Call after a save so the saved values stop counting as changes.
  const markFormClean = useCallback(() => {
    form.reset(form.getValues(), { keepValues: true });
  }, [form]);

  // Skip the request when the draft exists and nothing changed since the last save.
  const needsSave = !endorsementIdState || hasDirtyFields;

  // Two exceptions to "the response never feeds the form":
  // - Additional interests: take the backend-assigned ids so the next save
  //   updates rather than re-creates them.
  // - Hidden coverages are cleared, or a stale pick (e.g. trip collision before
  //   Modular) would override the backend's value once offered again.
  const saveEndorsement = useCallback(
    async (
      values: EndorsementFormValues,
      thunk: typeof createOrUpdateEndorsement = createOrUpdateEndorsement
    ) => {
      const saved = await dispatch(
        thunk({
          values,
          unitId,
          savedValues: form.formState
            .defaultValues as Partial<EndorsementFormValues>,
        })
      ).unwrap();

      if (saved.additionalInterests) {
        form.setValue("additionalInterests", saved.additionalInterests);
      }

      clearHiddenExtraCoverages(form, saved);
    },
    [dispatch, form, unitId]
  );

  const handleNext = useMemo(
    () =>
      form.handleSubmit(async (values) => {
        if (isRecalculating) return;

        if (currentStep === EndorsementRoutes.INSURED_INFO) {
          const serializedDate =
            values.endorsementEffectiveDate instanceof Date
              ? values.endorsementEffectiveDate.toISOString()
              : values.endorsementEffectiveDate;
          dispatch(
            setInsuredTabDetails({
              ...values,
              endorsementEffectiveDate: serializedDate,
            })
          );
        }

        if (
          currentStep === EndorsementRoutes.INSURED_INFO ||
          currentStep === EndorsementRoutes.COVERAGE
        ) {
          if (needsSave) {
            try {
              await saveEndorsement(values);
            } catch (e) {
              Toast.showError(
                currentStep === EndorsementRoutes.COVERAGE
                  ? getEndorsementErrorMessage(e)
                  : "Failed to save the endorsement. Please try again."
              );
              return;
            }
          }
          markFormClean();
        }

        if (
          currentStep === EndorsementRoutes.SUMMARY &&
          shouldCheckUnderwriting
        ) {
          const canContinue = await checkUnderwriting();
          if (!canContinue) return;
        }

        if (isLastStep) {
          try {
            await saveEndorsement(values);

            markFormClean();
            Toast.showSuccess("Endorsement issued successfully.");
            onClose();
          } catch {
            Toast.showError("Failed to issue endorsement. Please try again.");
          }
          return;
        }

        await navigateToEndorsementStep(navigate, policyLocator, nextStep, {
          mode,
          endorsementId,
        });
      }),
    [
      form,
      isRecalculating,
      currentStep,
      isLastStep,
      mode,
      markFormClean,
      needsSave,
      saveEndorsement,
      dispatch,
      shouldCheckUnderwriting,
      checkUnderwriting,
      navigate,
      policyLocator,
      endorsementId,
      nextStep,
      onClose,
    ]
  );

  const saveDraft = useCallback(
    async (values: EndorsementFormValues) => {
      if (needsSave) {
        try {
          await saveEndorsement(values);
        } catch {
          Toast.showError("Failed to save draft. Please try again.");
          return false;
        }
        markFormClean();
      }

      Toast.showSuccess("Draft saved successfully.");
      return true;
    },
    [saveEndorsement, markFormClean, needsSave]
  );

  const handleSave = useCallback(async () => {
    await form.handleSubmit(saveDraft)();
  }, [form, saveDraft]);

  const handleSaveAsDraftAndClose = useCallback(async () => {
    await form.handleSubmit(async (values) => {
      if (await saveDraft(values)) {
        onClose();
      } else {
        onSaveDraftError();
      }
    }, onSaveDraftError)();
  }, [form, saveDraft, onClose, onSaveDraftError]);

  // `checkPrice` saves too, so clean the form or the next Continue re-posts.
  // It shows its own error toast.
  const handleReprice = useCallback(async () => {
    await form.handleSubmit((values) =>
      saveEndorsement(values, checkPrice)
        .then(markFormClean)
        .catch(() => undefined)
    )();
  }, [form, saveEndorsement, markFormClean]);

  useImperativeHandle(
    ref,
    () => ({
      handleNext,
      handleSave,
      handleSaveAsDraftAndClose,
      handleReprice,
    }),
    [handleNext, handleSave, handleSaveAsDraftAndClose, handleReprice]
  );

  return (
    <FormProvider {...form}>
      <Outlet
        context={
          {
            form,
            policy,
            close: onClose,
            saveAsDraftAndClose: handleSaveAsDraftAndClose,
          } satisfies EndorsementOutletContext
        }
      />
    </FormProvider>
  );
});
